import { useEffect, useRef } from "react";
import ProductCard from "./ProductCard";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, FreeMode, Mousewheel } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import { useProducts } from "@/context/ProductsContext";
import { useSite } from "@/context/SiteContext";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function NewArrivals() {
  const { products, loading } = useProducts();
  const { settings } = useSite();
  const swiperRef = useRef(null);

  // show ALL published products, in the order the backend already sorted them
  // (home_order asc first, then remaining by created_at asc)
  const items = products;

  if (loading) return (
    <section className="py-24 md:py-32 bg-sand/40 overflow-hidden" data-testid="new-arrivals-loading">
      <div className="container-lux">
        <div className="h-3 w-24 bg-muted animate-pulse" />
        <div className="mt-4 h-12 w-64 bg-muted animate-pulse" />
      </div>
      <div className="mt-14 pl-6 md:pl-12 lg:pl-16 flex gap-4 md:gap-6 overflow-hidden">
        {Array.from({ length: 4 }).map((_, i) => <div key={i} className="flex-shrink-0 w-[78vw] sm:w-[42vw] lg:w-[25vw] aspect-[3/4] bg-muted animate-pulse" />)}
      </div>
    </section>
  );
  if (!items.length) return null;

  return (
    <section className="py-24 md:py-32 bg-sand/40 overflow-hidden" data-testid="new-arrivals">
      <div className="container-lux">
        <div className="flex items-end justify-between mb-14 gap-6">
          <div>
            <p className="overline text-muted-foreground">{settings.home.new_arrivals_kicker}</p>
            <h2 className="mt-4 font-serif tracking-tighter text-4xl md:text-6xl font-light">{settings.home.new_arrivals_title}</h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => swiperRef.current?.slidePrev()}
              className="w-11 h-11 border border-ink hover:bg-ink hover:text-ivory transition-colors flex items-center justify-center"
              data-testid="na-prev"
              aria-label="Previous"
            ><ArrowLeft size={14} strokeWidth={1} /></button>
            <button
              onClick={() => swiperRef.current?.slideNext()}
              className="w-11 h-11 border border-ink hover:bg-ink hover:text-ivory transition-colors flex items-center justify-center"
              data-testid="na-next"
              aria-label="Next"
            ><ArrowRight size={14} strokeWidth={1} /></button>
            <Link to="/collections/new" className="lux-link text-ink hidden md:inline-block ml-4" data-testid="see-all-new">See All</Link>
          </div>
        </div>
      </div>

      <div className="pl-6 md:pl-12 lg:pl-16">
        <Swiper
          onSwiper={(s) => (swiperRef.current = s)}
          modules={[Autoplay, FreeMode, Mousewheel]}
          slidesPerView={1.2}
          spaceBetween={16}
          freeMode={{ enabled: true, momentum: true, momentumBounce: false }}
          mousewheel={{ forceToAxis: true, sensitivity: 0.6 }}
          speed={900}
          loop={items.length > 4}
          autoplay={{ delay: 3200, disableOnInteraction: false, pauseOnMouseEnter: true }}
          breakpoints={{
            640:  { slidesPerView: 2.2, spaceBetween: 20 },
            1024: { slidesPerView: 3.2, spaceBetween: 28 },
            1280: { slidesPerView: 4.2, spaceBetween: 36 },
          }}
          className="!pr-6 md:!pr-12 lg:!pr-16"
        >
          {items.map((p) => (
            <SwiperSlide key={p.id} className="!h-auto">
              <ProductCard product={p} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
}
